import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Search } from 'lucide-react';
import { useLang } from '../../../context/LangContext';
import { GlassCard } from '../../shared/GlassCard';
import { useTypewriter } from '../../../hooks/useTypewriter';
import { SPRING } from '../../../animations/variants';

const keywordMap = {
  // E-commerce
  "e-commerce|ecommerce|commerce|boutique|shop|store": {
    toneKey: "secteurs.meta.1.tone",
    badge: "E-commerce",
    badgeEn: "E-commerce",
    color: "#54A133",
    clientKey: "secteurs.meta.1.client",
    entityKey: "secteurs.meta.1.entity",
    stats: ["0.2s", "FR", "secteurs.response.tone"],
  },
  // Santé / Healthcare
  "santé|sante|clinique|médecin|medecin|health|clinic|doctor|hospital|hôpital|hopital": {
    toneKey: "secteurs.meta.2.tone",
    badge: "Santé",
    badgeEn: "Healthcare",
    color: "#5399C2",
    clientKey: "secteurs.meta.2.client",
    entityKey: "secteurs.meta.2.entity",
    stats: ["0.2s", "FR", "secteurs.response.tone"],
  },
  // Banque & Finance
  "banque|finance|fonds|fund|bank|assurance|insurance": {
    toneKey: "secteurs.meta.3.tone",
    badge: "Banque & Finance",
    badgeEn: "Banking & Finance",
    color: "#E0C568",
    clientKey: "secteurs.meta.3.client",
    entityKey: "secteurs.meta.3.entity",
    stats: ["0.1s", "FR", "secteurs.response.tone"],
  },
  // Automobile
  "automobile|auto|voiture|car|véhicule|vehicule|garage": {
    badge: "Automobile",
    badgeEn: "Automotive",
    color: "#54A133",
    clientKey: "secteurs.meta.1.client",
    entityKey: "secteurs.default.entity",
    stats: ["0.3s", "FR", "secteurs.response.tone"],
  },
  // Éducation
  "éducation|education|école|ecole|formation|training|school|university|université": {
    badge: "Éducation",
    badgeEn: "Education",
    color: "#5399C2",
    clientKey: "secteurs.meta.2.client",
    entityKey: "secteurs.default.entity",
    stats: ["0.3s", "FR", "secteurs.response.tone"],
  },
  // Voyage & Tourisme
  "voyage|tourisme|travel|tourism|agence de voyage|vacances|holiday": {
    badge: "Voyage & Tourisme",
    badgeEn: "Travel & Tourism",
    color: "#E0C568",
    clientKey: "secteurs.meta.1.client",
    entityKey: "secteurs.default.entity",
    stats: ["0.2s", "FR", "secteurs.response.tone"],
  },
  // Restauration
  "restauration|restaurant|café|cafe|bar|brasserie|food": {
    badge: "Restauration",
    badgeEn: "Restaurant",
    color: "#54A133",
    clientKey: "secteurs.meta.1.client",
    entityKey: "secteurs.default.entity",
    stats: ["0.2s", "FR", "secteurs.response.tone"],
  },
  // Immobilier
  "immobilier|real estate|agence immobilière|agence immobiliere|logement|housing": {
    badge: "Immobilier",
    badgeEn: "Real Estate",
    color: "#5399C2",
    clientKey: "secteurs.meta.1.client",
    entityKey: "secteurs.default.entity",
    stats: ["0.3s", "FR", "secteurs.response.tone"],
  },
  // Juridique
  "juridique|legal|avocat|lawyer|notaire|droit|law": {
    badge: "Juridique",
    badgeEn: "Legal",
    color: "#E0C568",
    clientKey: "secteurs.meta.2.client",
    entityKey: "secteurs.default.entity",
    stats: ["0.3s", "FR", "secteurs.response.tone"],
  },
  // Consulting
  "consulting|conseil|consultant|advisory": {
    badge: "Consulting",
    badgeEn: "Consulting",
    color: "#54A133",
    clientKey: "secteurs.meta.2.client",
    entityKey: "secteurs.default.entity",
    stats: ["0.3s", "FR", "secteurs.response.tone"],
  },
  // Industrie
  "industrie|manufacturing|usine|factory|production": {
    badge: "Industrie",
    badgeEn: "Manufacturing",
    color: "#5399C2",
    clientKey: "secteurs.meta.1.client",
    entityKey: "secteurs.default.entity",
    stats: ["0.3s", "FR", "secteurs.response.tone"],
  },
  // ONG & Associations
  "ong|association|nonprofit|ngo|caritatif|charity|humanitaire": {
    badge: "ONG & Associations",
    badgeEn: "NGO & Nonprofits",
    color: "#54A133",
    clientKey: "secteurs.meta.2.client",
    entityKey: "secteurs.default.entity",
    stats: ["0.3s", "FR", "secteurs.response.tone"],
  },
  // Agriculture
  "agriculture|agricole|farm|ferme|agri": {
    badge: "Agriculture",
    badgeEn: "Agriculture",
    color: "#5399C2",
    clientKey: "secteurs.meta.1.client",
    entityKey: "secteurs.default.entity",
    stats: ["0.3s", "FR", "secteurs.response.tone"],
  },
  // Divertissement
  "divertissement|entertainment|musique|music|spectacle|event|événement": {
    badge: "Divertissement",
    badgeEn: "Entertainment",
    color: "#E0C568",
    clientKey: "secteurs.meta.1.client",
    entityKey: "secteurs.default.entity",
    stats: ["0.2s", "FR", "secteurs.response.tone"],
  },
  // Photographie
  "photographie|photo|photographer|photographe": {
    badge: "Photographie",
    badgeEn: "Photography",
    color: "#54A133",
    clientKey: "secteurs.meta.1.client",
    entityKey: "secteurs.default.entity",
    stats: ["0.3s", "FR", "secteurs.response.tone"],
  },
  // Fitness & Sport
  "fitness|sport|gym|salle de sport|coach|entrainement": {
    badge: "Fitness & Sport",
    badgeEn: "Fitness & Sports",
    color: "#5399C2",
    clientKey: "secteurs.meta.1.client",
    entityKey: "secteurs.default.entity",
    stats: ["0.2s", "FR", "secteurs.response.tone"],
  },
  // Art & Design
  "art|design|graphisme|graphic|créatif|creatif|creative": {
    badge: "Art & Design",
    badgeEn: "Art & Design",
    color: "#E0C568",
    clientKey: "secteurs.meta.1.client",
    entityKey: "secteurs.default.entity",
    stats: ["0.3s", "FR", "secteurs.response.tone"],
  },
  // Télécom & IT
  "telecom|télécom|télécommunication|telecommunication|it|informatique|tech|technology|telecom it|télécom it": {
    badge: "Télécom & IT",
    badgeEn: "Telecom & IT",
    color: "#54A133",
    clientKey: "secteurs.meta.1.client",
    entityKey: "secteurs.default.entity",
    stats: ["0.2s", "FR", "secteurs.response.tone"],
  },
  // Sécurité
  "sécurité|securite|security|surveillance|protection": {
    badge: "Sécurité",
    badgeEn: "Security",
    color: "#5399C2",
    clientKey: "secteurs.meta.2.client",
    entityKey: "secteurs.default.entity",
    stats: ["0.3s", "FR", "secteurs.response.tone"],
  },
  // Petite Enfance
  "petite enfance|childcare|crèche|creche|garde d'enfant|enfant|baby|bébé": {
    badge: "Petite Enfance",
    badgeEn: "Childcare",
    color: "#E0C568",
    clientKey: "secteurs.meta.2.client",
    entityKey: "secteurs.default.entity",
    stats: ["0.3s", "FR", "secteurs.response.tone"],
  },
  // Animalerie
  "animalerie|pet|animal|vétérinaire|veterinaire|vet|chien|chat|dog|cat": {
    badge: "Animalerie",
    badgeEn: "Pet Services",
    color: "#54A133",
    clientKey: "secteurs.meta.1.client",
    entityKey: "secteurs.default.entity",
    stats: ["0.3s", "FR", "secteurs.response.tone"],
  },
  // Mode & Textile
  "mode|fashion|textile|vêtement|vetement|clothing|prêt-à-porter": {
    badge: "Mode & Textile",
    badgeEn: "Fashion & Textile",
    color: "#5399C2",
    clientKey: "secteurs.meta.1.client",
    entityKey: "secteurs.default.entity",
    stats: ["0.2s", "FR", "secteurs.response.tone"],
  },
  // Joaillerie & Luxe
  "joaillerie|bijou|luxe|jewelry|luxury|montre|watch": {
    badge: "Joaillerie & Luxe",
    badgeEn: "Jewelry & Luxury",
    color: "#E0C568",
    clientKey: "secteurs.meta.1.client",
    entityKey: "secteurs.default.entity",
    stats: ["0.2s", "FR", "secteurs.response.tone"],
  },
  // Services Techniques
  "services techniques|technical|plombier|plumber|électricien|electricien|artisan": {
    badge: "Services Techniques",
    badgeEn: "Technical Services",
    color: "#54A133",
    clientKey: "secteurs.meta.1.client",
    entityKey: "secteurs.default.entity",
    stats: ["0.3s", "FR", "secteurs.response.tone"],
  },
  // Logistique
  "logistique|logistics|transport|livraison|delivery|supply chain": {
    badge: "Logistique",
    badgeEn: "Logistics",
    color: "#5399C2",
    clientKey: "secteurs.meta.1.client",
    entityKey: "secteurs.default.entity",
    stats: ["0.2s", "FR", "secteurs.response.tone"],
  },
  // Hôtellerie
  "hôtellerie|hotellerie|hotel|hôtel|spa|resort": {
    badge: "Hôtellerie",
    badgeEn: "Hospitality",
    color: "#E0C568",
    clientKey: "secteurs.meta.1.client",
    entityKey: "secteurs.default.entity",
    stats: ["0.2s", "FR", "secteurs.response.tone"],
  },
  // Beauté & Bien-être
  "beauté|beaute|beauty|bien-être|bien etre|wellness|coiffeur|coiffure|esthétique": {
    badge: "Beauté & Bien-être",
    badgeEn: "Beauty & Wellness",
    color: "#54A133",
    clientKey: "secteurs.meta.1.client",
    entityKey: "secteurs.default.entity",
    stats: ["0.2s", "FR", "secteurs.response.tone"],
  },
  // Édition & Média
  "édition|edition|média|media|presse|press|journal|publishing": {
    badge: "Édition & Média",
    badgeEn: "Publishing & Media",
    color: "#5399C2",
    clientKey: "secteurs.meta.1.client",
    entityKey: "secteurs.default.entity",
    stats: ["0.3s", "FR", "secteurs.response.tone"],
  },
  // Tech & Startups
  "startup|tech|saas|software|application|app|digital": {
    badge: "Tech & Startups",
    badgeEn: "Tech & Startups",
    color: "#E0C568",
    clientKey: "secteurs.meta.1.client",
    entityKey: "secteurs.default.entity",
    stats: ["0.2s", "FR", "secteurs.response.tone"],
  },
};

const defaultResult = {
  badge: "secteurs.default.badge",
  color: "#54A133",
  entityKey: "secteurs.default.entity",
  stats: ["0.3s", "FR", "secteurs.response.tone"],
};

// Personalized responses for better conversion
const sectorResponses = {
  "E-commerce": {
    fr: "Parfait ! Pour l'e-commerce, notre Agent IA gère le suivi des commandes, répond aux questions produits et qualifie vos leads 24h/24. Votre secteur est déjà intégré.",
    en: "Perfect! For e-commerce, our AI Agent handles order tracking, answers product questions and qualifies your leads 24/7. Your sector is already integrated."
  },
  "Santé": {
    fr: "Excellent choix ! En santé, notre Agent IA gère les prises de RDV, le rappel de consultations et l'orientation patients. 100% confidentiel et conforme RGPD.",
    en: "Excellent choice! In healthcare, our AI Agent handles appointments, consultation reminders and patient guidance. 100% confidential and GDPR compliant."
  },
  "Banque & Finance": {
    fr: "Le secteur bancaire est notre spécialité. Qualification de leads, support client sécurisé, suivi de dossiers. Notre Agent IA s'intègre parfaitement à vos process.",
    en: "The banking sector is our specialty. Lead qualification, secure customer support, case tracking. Our AI Agent integrates perfectly with your processes."
  },
  "Télécom & IT": {
    fr: "Télécom & IT : notre Agent IA excelle en support technique niveau 1, qualification de tickets et assistance utilisateurs. Réduction de charge garantie.",
    en: "Telecom & IT: our AI Agent excels in level 1 tech support, ticket qualification and user assistance. Guaranteed workload reduction."
  },
  "Automobile": {
    fr: "Dans l'automobile, notre Agent IA génère des devis instantanés, planifie les essais et assure le suivi après-vente 24/7.",
    en: "In automotive, our AI Agent generates instant quotes, schedules test drives and provides 24/7 after-sales support."
  },
  "Immobilier": {
    fr: "Immobilier : notre Agent IA qualifie les acquéreurs, planifie les visites et relance automatiquement les prospects intéressés.",
    en: "Real Estate: our AI Agent qualifies buyers, schedules visits and automatically follows up with interested prospects."
  },
  "Juridique": {
    fr: "Pour les cabinets juridiques, notre Agent IA pré-qualifie les dossiers, gère la prise de RDV et assure un premier niveau d'information client.",
    en: "For law firms, our AI Agent pre-qualifies cases, manages appointments and provides first-level client information."
  },
  "Assurance": {
    fr: "En assurance, notre Agent IA gère les déclarations de sinistres, oriente vers les bons interlocuteurs et assure le suivi des dossiers 24/7.",
    en: "In insurance, our AI Agent handles claims, routes to the right contacts and ensures 24/7 case follow-up."
  },
  "ONG & Associations": {
    fr: "Pour les ONG, notre Agent IA gère les dons, répond aux questions des bénévoles et communique sur vos actions. Impact démultiplié.",
    en: "For NGOs, our AI Agent manages donations, answers volunteer questions and communicates about your actions. Multiplied impact."
  },
  "Restauration": {
    fr: "Restauration : commandes par téléphone, réservations automatisées, service client 24/7. Notre Agent IA transforme votre relation client.",
    en: "Restaurant: phone orders, automated reservations, 24/7 customer service. Our AI Agent transforms your customer relationship."
  },
  "Hôtellerie": {
    fr: "Hôtellerie : réservations vocales, conciergerie 24/7, gestion des demandes clients. Notre Agent IA élève votre service.",
    en: "Hospitality: voice reservations, 24/7 concierge, guest request management. Our AI Agent elevates your service."
  },
  "Éducation": {
    fr: "Éducation : inscriptions en ligne, suivi pédagogique, support administratif. Notre Agent IA libère vos équipes pour l'essentiel.",
    en: "Education: online enrollment, academic tracking, administrative support. Our AI Agent frees your teams for what matters."
  },
  "Fitness & Sport": {
    fr: "Fitness : planning des cours, inscriptions, renouvellements d'abonnements. Notre Agent IA optimise votre relation adhérents.",
    en: "Fitness: class scheduling, registrations, subscription renewals. Our AI Agent optimizes your member relations."
  },
  "Beauté & Bien-être": {
    fr: "Beauté & Bien-être : prise de RDV salon, rappels automatiques, conseil personnalisé. Notre Agent IA fidélise votre clientèle.",
    en: "Beauty & Wellness: salon appointments, automatic reminders, personalized advice. Our AI Agent builds customer loyalty."
  },
  "Logistique": {
    fr: "Logistique : suivi de livraison en temps réel, gestion des réclamations, coordination transporteurs. Notre Agent IA fluidifie vos opérations.",
    en: "Logistics: real-time delivery tracking, claims management, carrier coordination. Our AI Agent streamlines your operations."
  }
};

const getPersonalizedResponse = (badge, lang) => {
  const responses = sectorResponses[badge];
  if (responses) {
    return lang === 'en' ? responses.en : responses.fr;
  }
  return null;
};

const CameleonResponse = ({ result, t, lang }) => {
  // Use personalized response if available, otherwise use translation
  const badgeText = result.badge?.startsWith?.("secteurs.") ? t(result.badge) : (lang === 'en' && result.badgeEn ? result.badgeEn : result.badge);
  const personalizedText = getPersonalizedResponse(result.badge, lang);
  const entityText = personalizedText || t(result.entityKey);
  const { displayed } = useTypewriter(entityText, 25, true);

  return (
    <GlassCard className="p-5 mt-6" hover={false}>
      <div className="flex items-center gap-2 mb-4" style={{ borderColor: `${result.color}30` }}>
        <Sparkles size={16} style={{ color: result.color }} />
        <span className="text-xs font-medium px-2.5 py-1 rounded-full border" style={{ color: result.color, borderColor: `${result.color}40`, backgroundColor: `${result.color}10` }}>
          {badgeText}
        </span>
      </div>
      {result.clientKey && (
        <div className="flex justify-end mb-2">
          <div className="bg-brand-green/20 border border-brand-green/20 text-sm text-neutral-200 px-3 py-2 rounded-2xl rounded-tr-md max-w-[85%]">
            {t(result.clientKey)}
          </div>
        </div>
      )}
      <div className="flex justify-start mb-3">
        <div className="bg-white/[0.05] border border-white/[0.08] text-sm text-neutral-300 px-3 py-2 rounded-2xl rounded-tl-md max-w-[85%]">
          {displayed}<span className="animate-pulse">|</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 pt-2">
        {result.stats.map((s, j) => (
          <span key={j} className="text-xs px-2.5 py-1 rounded-full bg-white/[0.04] border border-white/[0.08] text-neutral-500">
            {s.startsWith("secteurs.") ? t(s) : s}
          </span>
        ))}
      </div>
    </GlassCard>
  );
};

export const CameleonHero = () => {
  const { t, lang } = useLang();
  const [inputValue, setInputValue] = useState('');
  const [matchedResult, setMatchedResult] = useState(null);
  const placeholder = t("secteurs.search.placeholder");

  const findMatch = useCallback((val) => {
    const lower = val.toLowerCase();
    for (const [pattern, data] of Object.entries(keywordMap)) {
      const keywords = pattern.split("|");
      if (keywords.some(kw => lower.includes(kw))) return data;
    }
    return null;
  }, []);

  useEffect(() => {
    if (inputValue.length < 3) {
      setMatchedResult(null);
      return;
    }
    const timer = setTimeout(() => {
      const match = findMatch(inputValue);
      setMatchedResult(match || { ...defaultResult, badge: t(defaultResult.badge) });
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue, findMatch, t]);

  return (
    <section data-testid="cameleon-hero" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#030303] dot-grid pt-20">
      <motion.div className="absolute rounded-full blur-[100px] opacity-25 bg-brand-green w-80 h-80 top-1/4 right-1/4"
        animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }} />

      <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
        <motion.h1
          className="font-extrabold tracking-[-0.03em] leading-[1.05] mb-6"
          style={{ fontSize: 'clamp(42px, 7vw, 72px)' }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={SPRING}
        >
          <span className="text-white">{t("secteurs.hero.title.1")}</span>
          <br />
          <span className="gradient-text">{t("secteurs.hero.title.2")}</span>
        </motion.h1>

        <motion.p
          className="text-neutral-400 text-lg max-w-xl mx-auto leading-relaxed mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...SPRING, delay: 0.2 }}
        >
          {t("secteurs.hero.subtitle")}
        </motion.p>

        {/* Command Input */}
        <motion.div
          className="relative max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...SPRING, delay: 0.4 }}
        >
          <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-brand-green/30 to-brand-gold/30 blur-lg opacity-50" />
          <div className="relative flex items-center bg-white/[0.04] backdrop-blur-xl border border-white/[0.12] rounded-2xl px-5 py-4 focus-within:border-brand-green/50 transition-all">
            <Search size={20} className="text-neutral-500 mr-3 flex-shrink-0" />
            <input
              data-testid="cameleon-input"
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className="flex-1 bg-transparent text-white text-base outline-none placeholder:text-neutral-600"
              placeholder={placeholder}
            />
          </div>
        </motion.div>

        {/* Response block */}
        <AnimatePresence mode="wait">
          {matchedResult && (
            <motion.div
              key={matchedResult.badge}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <CameleonResponse result={matchedResult} t={t} lang={lang} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};
